/**
 * financialEngineUtil.js
 *
 * Client-side implementation of the Hybrid Cyber Risk Financial Engine.
 * Mirrors backend MonetaryImpactEngine v3.1 logic for modules that do not
 * store backend-calculated financial fields (DarkWeb, TPRM, Enterprise,
 * Ransomware, BusinessUnit).
 *
 * Pipeline:
 *   Per-finding : baseline lookup → size/regulatory multipliers → severity
 *                 factor → MaxCombinedMultiplier cap → three-band SLE
 *   Portfolio   : risk-type grouping → MAX deduplication → log dampening
 *                 → probability weighting  (runs independently for Low,
 *                 Expected, High bands)
 *
 * All formula constants are drawn from the Hybrid Cyber Risk Financial Engine
 * Documentation v3.1.
 */

// ── Engine constants ───────────────────────────────────────────────────────────

/** Hard cap on (combinedMultiplier × severityFactor). Default 1.8× (configurable on backend). */
const MAX_COMBINED_MULTIPLIER = 1.8;

/** Logarithmic dampening saturation cap. Prevents >15 findings in one type
 *  from inflating the portfolio beyond a defensible level. */
const SEVERITY_MULTIPLIER_CAP = 3.5;

/** Size multipliers keyed by the `companySize` parameter string.
 *  Matches the 8-tier backend table; frontend uses four summary tiers. */
const SIZE_MULTIPLIERS = {
  small: 0.70,      // 1–100 users
  medium: 1.00,     // 500–1,000 users (reference)
  large: 1.35,      // 5,000–10,000 users
  enterprise: 1.50, // 10,000+ / Enterprise
};

/** Regulatory multipliers by numeric vertical ID (matches FinancialImpactBaselines lookup). */
const REGULATORY_BY_ID = {
  1: 1.80, // Healthcare   — HIPAA, HITECH
  2: 1.70, // Finance      — SOX, PCI-DSS, GLBA
  3: 1.60, // Insurance    — NAIC, state laws
  4: 1.50, // Government   — FedRAMP, FISMA
  5: 1.30, // Education    — FERPA
  6: 1.20, // Retail       — PCI-DSS
  7: 1.00, // Technology   — GDPR, CCPA
  8: 1.00, // Manufacturing
};

/** Regulatory multipliers by industry name string (fallback when verticalId is absent). */
const REGULATORY_BY_NAME = {
  healthcare: 1.80,
  finance: 1.70,
  banking: 1.70,
  insurance: 1.60,
  government: 1.50,
  education: 1.30,
  retail: 1.20,
  technology: 1.00,
  manufacturing: 1.00,
};

/** FAIR-aligned annual probability of materialisation per risk-type code. */
const ANNUAL_PROBABILITIES = {
  RA: 0.23,  // Unauthorized Access
  DB: 0.20,  // Data Breach
  RW: 0.23,  // Ransomware
  AI: 0.12,  // AI System Risk
  FF: 0.15,  // Financial Fraud
  IP: 0.10,  // IP Theft
  SC: 0.12,  // Supply Chain
  CM: 0.14,  // Cloud Misconfiguration
  IT: 0.12,  // Insider Threat
  TPE: 0.12, // Third-Party Exposure
};
const DEFAULT_PROBABILITY = 0.10;

/**
 * AI severity factors (maps risk rating → bounded multiplier).
 * Applied to the deterministic baseline SLE before the combined-multiplier cap.
 */
const SEVERITY_FACTORS = {
  CRITICAL: 1.20,
  HIGH: 1.05,
  MEDIUM: 0.90,
  LOW: 0.75,
};

/**
 * Baseline most-likely SLE per severity rating.
 * Represents a reference organisation (500-1,000 users, Technology vertical,
 * no revenue adjustment).  These are the client-side fallback values used when
 * the backend FinancialImpactBaselines table is not directly accessible.
 */
const BASELINE_MOST_LIKELY = {
  CRITICAL: 500_000,
  HIGH: 200_000,
  MEDIUM: 75_000,
  LOW: 25_000,
};

/** BaselineMin  = BaselineMostLikely × 0.35  (conservative lower bound) */
const BASELINE_MIN_RATIO = 0.35;
/** BaselineMax  = BaselineMostLikely × 2.80  (heavy-tail upper bound) */
const BASELINE_MAX_RATIO = 2.80;

/** Fixed cost-category allocation ratios (unchanged from existing code). */
const CATEGORY_PCTS = {
  direct_costs: 0.30,
  business_interruption: 0.25,
  recovery_costs: 0.20,
  regulatory_fines: 0.15,
  reputation_damage: 0.10,
};

// ── Internal helpers ───────────────────────────────────────────────────────────

/** Resolve the regulatory multiplier from either a numeric verticalId or an industry name string. */
function resolveRegMultiplier(verticalId, industryName) {
  if (verticalId != null) {
    return REGULATORY_BY_ID[Number(verticalId)] ?? 1.00;
  }
  if (industryName) {
    return REGULATORY_BY_NAME[(industryName || '').toLowerCase().trim()] ?? 1.00;
  }
  return 1.00;
}

/**
 * Compute three-band SLE (Low / Expected / High) for a single finding.
 *
 * @param {string} rating            - CRITICAL | HIGH | MEDIUM | LOW
 * @param {number} combinedMultiplier - sizeMultiplier × regulatoryMultiplier × revenueMultiplier
 * @returns {{ sleLow, sleExpected, sleHigh, effectiveSeverityFactor, capped, appliedMultiplier }}
 */
function computeSLEBands(rating, combinedMultiplier) {
  const r = (rating || '').toUpperCase();
  const severityFactor = SEVERITY_FACTORS[r] ?? 1.00;

  const baselineMostLikely = BASELINE_MOST_LIKELY[r] ?? 10_000;
  const baselineMin = baselineMostLikely * BASELINE_MIN_RATIO;
  const baselineMax = baselineMostLikely * BASELINE_MAX_RATIO;

  // Apply MaxCombinedMultiplier cap (Step 4 of the deterministic pipeline)
  let effectiveSeverityFactor = severityFactor;
  let capped = false;
  if (combinedMultiplier * severityFactor > MAX_COMBINED_MULTIPLIER) {
    effectiveSeverityFactor = MAX_COMBINED_MULTIPLIER / combinedMultiplier;
    capped = true;
  }

  // Three-band SLE — all bands share the same effectiveSeverityFactor (Step 5)
  return {
    sleLow: baselineMin * combinedMultiplier * effectiveSeverityFactor,
    sleExpected: baselineMostLikely * combinedMultiplier * effectiveSeverityFactor,
    sleHigh: baselineMax * combinedMultiplier * effectiveSeverityFactor,
    effectiveSeverityFactor,
    capped,
    appliedMultiplier: combinedMultiplier,
  };
}

/**
 * Run the portfolio aggregation pipeline for one SLE band.
 * Implements Steps A–D from the documentation:
 *   A. Risk-type grouping (caller builds the Map)
 *   B. MAX deduplication
 *   C. Logarithmic dampening   (min(1 + ln(n), 3.5))
 *   D. Probability weighting   (EAL = finalTypeImpact × annualProbability)
 *
 * @param {Map<string, number[]>} typeGroups - riskType → [SLE values for this band]
 * @returns {{ totalEAL: number, maxExposure: number }}
 */
function runPipeline(typeGroups) {
  let totalEAL = 0;
  let maxExposure = 0;

  for (const [riskType, sles] of typeGroups) {
    const n = sles.length;

    // B — MAX deduplication: only the worst-case event can materialise from a correlated group
    const maxSLE = Math.max(...sles);

    // C — Logarithmic dampening: corroborating findings increase frequency confidence, not loss
    const dampingFactor = Math.min(1 + Math.log(n), SEVERITY_MULTIPLIER_CAP);
    const finalTypeImpact = maxSLE * dampingFactor;

    // D — Annual probability weighting
    const prob = ANNUAL_PROBABILITIES[riskType] ?? DEFAULT_PROBABILITY;
    totalEAL += finalTypeImpact * prob;
    maxExposure += finalTypeImpact;
  }

  return { totalEAL, maxExposure };
}

// ── FAIR version detection ─────────────────────────────────────────────────────

/**
 * Returns true for any FAIR-aligned financial engine version (fair-align-v1.0,
 * fair-align-v1.1, future bumps).  Change this one function if the prefix ever changes.
 *
 * @param {string|null|undefined} version - risk.financialEngineVersion
 */
export const isFairAligned = (version) => version?.startsWith('fair-align-') === true;

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Compute portfolio financial exposure using the Hybrid Engine logic.
 *
 * Produces an `analysisResult` object that is backward-compatible with the
 * existing `setAnalysis()` calls (preserving `total_potential_impact`,
 * `risk_count`, `breakdown`, `parameters`, `recommendations`) and adds
 * scenario-band keys consumed by enhanced UI sections.
 *
 * @param {Array}    risks
 *   Array of risk objects from the module's API response.
 *
 * @param {Object}   opts
 * @param {string}   opts.companySize
 *   "small" | "medium" | "large" | "enterprise" — from the parameters state.
 * @param {number|null} [opts.verticalId]
 *   Numeric vertical ID (1–8).  Takes precedence over industryName.
 * @param {string|null} [opts.industryName]
 *   Fallback industry name string (e.g. "Finance", "Healthcare").
 * @param {Function} opts.getRating
 *   `(risk) => string` — extracts the severity rating from a risk object.
 *   Should return one of: CRITICAL | HIGH | MEDIUM | LOW (case-insensitive).
 * @param {Function} [opts.getRiskType]
 *   `(risk) => string` — extracts the risk-type code (RA|DB|RW|...).
 *   Defaults to returning "DB" for all risks.
 * @param {Object}   opts.parameters
 *   The `parameters` state object; passed through unchanged into the result.
 * @param {Function} [opts.generateRecommendations]
 *   `(totalImpact, riskCount) => string[]`  — the component's recommendation
 *   generator.  When omitted, recommendations is an empty array.
 *
 * @returns {Object} analysisResult
 */
export function computePortfolioExposure(risks, opts) {
  const {
    companySize = 'medium',
    verticalId = null,
    industryName = null,
    getRating,
    getRiskType = () => 'DB',
    parameters,
    generateRecommendations,
  } = opts;

  // ── Multiplier resolution ──────────────────────────────────────────────────
  const sizeMultiplier = SIZE_MULTIPLIERS[companySize] ?? 1.00;
  const regulatoryMultiplier = resolveRegMultiplier(verticalId, industryName);
  const revenueMultiplier = 1.00; // pending revenue-band integration (field reserved)
  const combinedMultiplier = sizeMultiplier * regulatoryMultiplier * revenueMultiplier;

  // ── Per-finding SLE computation — build three band groups ─────────────────
  const groupsExpected = new Map(); // riskType → [sleExpected, ...]
  const groupsLow = new Map();      // riskType → [sleLow, ...]
  const groupsHigh = new Map();     // riskType → [sleHigh, ...]

  (risks || []).forEach((risk) => {
    const rating = getRating(risk);
    const riskType = ((getRiskType(risk) || 'DB').trim().toUpperCase());

    const { sleLow, sleExpected, sleHigh } = computeSLEBands(rating, combinedMultiplier);

    if (!groupsExpected.has(riskType)) {
      groupsExpected.set(riskType, []);
      groupsLow.set(riskType, []);
      groupsHigh.set(riskType, []);
    }
    groupsExpected.get(riskType).push(sleExpected);
    groupsLow.get(riskType).push(sleLow);
    groupsHigh.get(riskType).push(sleHigh);
  });

  // ── Portfolio aggregation — three independent pipeline instances ───────────
  const { totalEAL: totalExpected, maxExposure } = runPipeline(groupsExpected);
  const { totalEAL: totalLow } = runPipeline(groupsLow);
  const { totalEAL: totalHigh } = runPipeline(groupsHigh);

  // ── Cost-category breakdown (Expected band) ────────────────────────────────
  const breakdown = Object.fromEntries(
    Object.entries(CATEGORY_PCTS).map(([k, pct]) => [k, totalExpected * pct])
  );
  const breakdown_low = Object.fromEntries(
    Object.entries(CATEGORY_PCTS).map(([k, pct]) => [k, totalLow * pct])
  );
  const breakdown_high = Object.fromEntries(
    Object.entries(CATEGORY_PCTS).map(([k, pct]) => [k, totalHigh * pct])
  );

  // ── Result ─────────────────────────────────────────────────────────────────
  return {
    // ── Backward-compatible keys (preserved for all existing JSX consumers) ──
    total_potential_impact: Math.round(totalExpected),
    risk_count: (risks || []).length,
    breakdown,
    parameters,
    recommendations: generateRecommendations
      ? generateRecommendations(totalExpected, (risks || []).length)
      : [],

    // ── Scenario-band keys (new — used by enhanced UI sections) ──────────────
    total_potential_impact_low: Math.round(totalLow),
    total_potential_impact_high: Math.round(totalHigh),
    max_exposure: Math.round(maxExposure),
    has_scenario_bands: totalLow !== totalExpected || totalHigh !== totalExpected,

    // ── Breakdown by band ─────────────────────────────────────────────────────
    breakdown_low,
    breakdown_high,

    // ── Multiplier audit trail ────────────────────────────────────────────────
    appliedMultiplier: combinedMultiplier,
    verticalAdjustmentUsed: regulatoryMultiplier,
    sizeMultiplierUsed: sizeMultiplier,
  };
}
